import React, { useContext } from 'react';
import Table, { HeaderRow, TableRow } from '../../common/Widgets/Table';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { Link, useNavigate } from 'react-router-dom';
import { SiteContext } from '../../../context/SiteContext';
import Links from '../../common/Widgets/Links';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { PageAbsolutePath } from '../../../consts/Common';

interface Props {
  isShowingInPopup?: boolean;
}

const BusinessTermsAncillaryPage = ({
  isShowingInPopup = false
}: Props): JSX.Element => {
  const navigate = useNavigate();

  const { site } = useContext(SiteContext);

  const { brand } = site;

  const { brandDisplayFullName, brandDisplayName, brandVanityUrl } = brand;

  const goBack = (): void => {
    navigate(-1);
  };

  const insuranceyTypeTableHeader: HeaderRow = {
    columns: ['Insurer Name', 'Type of Cover']
  };
  const insuranceTypeTableRows: TableRow[] = [
    {
      columns: [
        'Allianz ABC Insurance (formerly LV ABC Insurance)',
        'Annual motor insurance'
      ]
    },
    {
      columns: [
        'Allianz Highway Insurance (formerly LV Highway Insurance)',
        'Annual motor insurance'
      ]
    },
    {
      columns: ['Ageas Insurance Ltd', 'Annual motor insurance']
    },
    {
      columns: ['Covea Insurance Plc', 'Annual motor insurance']
    },
    {
      columns: ['ERS', 'Annual motor insurance']
    },
    {
      columns: [
        'KGM Underwriting Services Limited',
        'Free Driveaway insurance (from 28 January 2023)'
      ]
    },
    {
      columns: ['AXA', 'Annual motor insurance']
    },
    {
      columns: [
        'AmTrust Europe Limited',
        'Motor Legal Expenses Insurance, Courtesy Car Plus - 21 days (From 1 January 2023)'
      ]
    },
    {
      columns: [
        'HDI Global Speciality SE (administered by ARAG Plc)',
        'Courtesy Car Plus - 28 days (From 12 May 2023)'
      ]
    },
    {
      columns: [
        'Fortegra Europe Insurance Company Ltd',
        'Key Protection Insurance, Excess Insurance (From 12 May 2023)'
      ]
    },
    {
      columns: [
        'AWP P&C SA (part of Allianz Global Assistance)',
        'Breakdown Assistance'
      ]
    }
  ];

  const transactionTypeTableHeader: HeaderRow = {
    columns: ['Type of Transaction', 'Service Charge']
  };

  const transactionTypeTableRows: TableRow[] = [
    {
      columns: ['Arrangement of a new annual motor insurance policy', '£40.00']
    },
    {
      columns: [
        'Arrangement of a policy renewal of annual motor insurance',
        '£40.00'
      ]
    },
    {
      columns: [
        'Any changes to your existing annual motor insurance policy',
        '£40.00'
      ]
    },
    {
      columns: [
        'Cancellation of the annual motor insurance policy within the cooling off period',
        '£0.00'
      ]
    },
    {
      columns: [
        'Cancellation of the annual motor insurance policy after the cooling off period',
        '£40.00'
      ]
    }
  ];

  const chevronLeftComponent = <FontAwesomeIcon icon={faChevronLeft} />;

  return (
    <div>
      {!isShowingInPopup && (
        <Links
          id='back-link'
          title=''
          items={[{ to: '', text: 'Back', onClick: goBack }]}
          icon={chevronLeftComponent}
        />
      )}

      <h2>Our Terms of Business with you</h2>
      <p>
        The Financial Conduct Authority ("FCA") is the independent watchdog that
        regulates financial services. Contained in this document is information
        which the FCA requires us to tell you. Use this information to decide if
        our services are right for you.
      </p>
      <h4>Who are we?</h4>
      <p>
        {`${brandDisplayFullName} Insurance is arranged by Car Care Plan Limited. 
        Car Care Plan Limited is a specialist company appointed by ${brandDisplayName} 
        to ensure its customers are able to obtain insurance policies and use accident
        claims services which guarantee to repair your vehicle at a ${brandDisplayName} 
        approved repairer using only genuine ${brandDisplayName} parts (our Repair guarantee). 
        For more information about Car Care Plan Limited please visit `}
        <a
          className='in-text'
          href='https://www.carcareplan.com'
          target='_blank'>
          www.carcareplan.com
        </a>
        {` We act on your behalf except where we collect or refund premiums which
        we will do on behalf of the insurer. When we sell you a policy the
        insurer pays us a percentage commission from the total premium. Where
        you opt to pay for your insurance by direct debit, we also act as a
        credit broker. We receive commission from the lender, Premium Credit
        Limited, for our credit broking arrangements with our customers.`}
      </p>
      <h4>Our Repair Guarantee</h4>
      <p>
        {`We guarantee that should you need to claim, we will ensure your vehicle is 
        repaired at a ${brandDisplayName} approved repairer using only genuine parts. 
        For glass claims only, our approved repairer is Auto Windscreens. In the event
        of a windscreen replacement, our approved repairer will need to organise for your
        vehicle to be re-calibrated at your nearest ${brandDisplayName} dealership and this 
        may be a separate/additional appointment dependent on. availability. Should there
        be a lack of availability or delay in obtaining genuine parts, or delays in arranging
        your repair due to availability, we are not responsible for:`}
      </p>
      <ul>
        <li>Expenses or additional costs that arise,</li>
        <li>Loss of use of your vehicle,</li>
        <li>
          Alternative transport costs or lack of a courtesy car (courtesy cars
          are subject to availability and do not apply to glass claims),
        </li>
        <li>
          Fuel costs for any courtesy car or hire car you are provided, or for
          travel to any approved repairer or dealership.
        </li>
      </ul>
      <h4>Whose policies do we offer?</h4>
      <p>
        In the case of annual motor insurance, we have a panel of insurers from
        whom we will get quotes. In the case of add-on products, we deal with
        single insurers for each product.
      </p>
      <p>A list of all the insurers we deal with is shown below:</p>
      <Table
        id='insurers-table'
        header={insuranceyTypeTableHeader}
        rows={insuranceTypeTableRows}
      />
      <h4>Which service will we provide you with?</h4>
      <p>
        You will not receive advice or a personal recommendation from us. We
        will provide you with information about the policy and you will need to
        make your own choice about how to proceed
      </p>
      <h4>Your right to cancel your policy and your 'cooling off' period</h4>
      <p>
        You have the following rights to cancel any policy you buy through us:
      </p>
      <ul>
        <li>
          You have a cancellation right which is set out in the policy document.
        </li>
        <li>
          You are also entitled to a 'cooling off' period, which ends 14 days
          after you receive your policy documents
        </li>
      </ul>
      <p>
        You can cancel at any time by contacting our customer service team or by
        writing to us (see your policy documents for details). Please note, if
        you send a written instruction by post, we will still need to speak to
        you.
      </p>
      <h4>What you will be charged if your insurance is cancelled</h4>
      <p>
        <u>
          In the case of annual motor insurance and any additional products:
        </u>
      </p>
      <ol>
        <li>
          Prior to the commencement date of your policy.
          <ul>
            <li>
              If your policy is cancelled prior to this date, a full refund of
              any monies paid less our original arrangement fee will be
              provided.
            </li>
          </ul>
        </li>
        <li>
          Within the 'cooling off' period.
          <ul>
            <li>
              If your policy is cancelled, before the end of the 'cooling off'
              period, you will be charged by the insurer for the service they
              have provided up to the point of cancellation, providing you have
              not made a claim. We do not make a cancellation charge but will
              retain our arrangement fee. Your insurer may apply a cancellation
              charge – see your Policy Document for details. Any additional
              products will be refunded in full providing you have not claimed
              against them.
            </li>
          </ul>
        </li>
        <li>
          After the 'cooling off' period
          <ul>
            <li>
              You will be charged by the insurer for the service they have
              provided up to the point of cancellation, providing you have not
              made a claim. Your insurer may apply a cancellation charge - see
              your Policy Document for details. We will also make a cancellation
              charge as per the tables below and our arrangement fee will not be
              refunded. Any additional products you have purchased will also be
              non-refundable.
            </li>
          </ul>
        </li>
      </ol>
      <p>
        Should you request an amendment to your policy which cannot be covered
        by your insurer, our cancellation fee still applies. In addition, if you
        have opted to pay by Direct Debit, upon cancellation of the policy, the
        full outstanding balance on the finance agreement will become payable
        less any eligible premium refund as described above.
      </p>
      <p>
        Where a cancellation or other transaction results in a premium refund
        being due to you, that refund will be reduced by any service charge and
        any other sums you owe in respect of the policy.
      </p>
      <h4>What will you pay us for our services?</h4>
      <p>
        The table below shows our charges only, applicable at the time of
        issuing.
      </p>
      <Table
        id='charges-table'
        header={transactionTypeTableHeader}
        rows={transactionTypeTableRows}
      />
      <p>
        In the event we must amend the policy due to non-disclosure or
        validation purposes (where we have verified the information provided is
        incorrect), our amendment fee still applies. Where a transaction would
        result in a refund to you or a payment you owe to us of less than £5,
        due to the cost of processing, this amount will be retained by us as an
        additional service charge or written off.
      </p>
      <h4>Payment of premium by Direct Debit (Premium Instalment Plan)</h4>
      <p>
        Where we arrange a premium instalment plan, we act as a credit broker on
        behalf of our customers. We do not act as the finance lender. By paying
        monthly you are entering into a finance agreement and therefore,
        interest will be payable as well as additional charges, making the
        overall cost more expensive than paying annually. Although we do not
        charge any fees for arranging your premium instalment plan, we do
        receive a commission from the lender for our credit broking arrangements
        with our customers.
      </p>
      <p>
        Our premium instalment plan is exclusively provided by Premium Credit
        Limited (‘Premium Credit’) and all Direct Debits are protected by the
        Direct Debit Guarantee, a copy of which will be sent with your finance
        documents. Your personal information and the bank details you provide to
        us will be passed to Premium Credit. Details of Premium Credits fees and
        charges will be detailed in the finance documentation sent to you.
      </p>
      <p>
        <u>The lender and your data</u>
      </p>
      <p>
        Premium Credit is authorised and regulated by the Financial Conduct
        Authority. They will act as a separate data controller of your personal
        data once your information is transferred to them. Credit is subject to
        status and Premium Credit may use one or more credit reference agencies
        to perform credit and identity checks on you. It is important that you
        read the Premium Credit privacy notice which includes information about
        how they will use your information including the credit and identity
        checks they perform on you with credit reference agencies and the
        footprints these leave on your credit files which can be seen by other
        lenders who may use this information to assess future applications. You
        may request details of the credit reference agencies used by contacting
        us or Premium Credit directly. Premium Credit will also use your details
        to set up a new Direct Debit Instruction and collect repayments by
        Direct Debit.
      </p>
      <p>
        <u>Refusal of credit</u>
      </p>
      <p>
        Premium Credit reserves the right to refuse credit. Should your
        application for credit be refused, Premium Credit will write to you
        confirming this and you will need to contact us to pay for your
        insurance in full as a one-off lump sum payment. If you opt not to
        continue the insurance policy after credit being refused, you will need
        to contact us to instruct cancellation. If you do, you will still be
        required to pay for the time you were insured including any applicable
        fees - see the 'What you will be charged if your insurance is cancelled'
        section of this document.
      </p>
      <p>
        <u>Acceptance of credit and charges</u>
      </p>
      <p>
        If your application for credit is accepted by Premium Credit, you will
        receive a welcome pack which includes a customer reference number, and a
        Credit Agreement between you and Premium Credit which you will need to
        sign online or by paper and return to them as instructed. Failure to
        return the Credit Agreement within the required timescale as stated in
        their welcome pack will result in a £25 administration charge. Premium
        Credit will also confirm the exact date of your first repayment, which
        will include a £5 facility fee, in writing.
      </p>
      <p>
        <u>Late and missed payments</u>
      </p>
      <p>
        Please be aware that failing to meet your instalment repayments will
        result in a default charge of £25 being applied by Premium Credit.
        Defaults may be registered on your credit file which can impact your
        future ability to obtain credit. There is also a charge of £25 applied
        by Premium Credit for late repayments and these may also be registered
        on your credit file which could impact your future ability to obtain
        credit. Full details of the charges applied will be detailed in your
        Credit Agreement.
      </p>
      <p>
        Failure to pay the instalments may result in Premium Credit cancelling
        your finance agreement and requesting the remaining balance to be paid.
        If this happens, we will also issue a 7-day notice of cancellation for
        your insurance policy. Your insurance cover will cease from the date
        advised in this letter. If we cancel your insurance for this reason, we
        will calculate any refund of premium in accordance with rules regarding
        cancellation set out in this document. Please note: We do not issue
        refunds of less than £5. Any refund due to you will be off- set against
        any balance that may still be outstanding on your premium instalment
        plan. We will not accept responsibility for cancellation of insurance
        due to late or non-payment of premium by customers.
      </p>
      <p>
        <u>Right to withdraw and cancellation</u>
      </p>
      <p>
        You have the right to withdraw from the Credit Agreement within 14 days
        and details of such cancellation rights will be contained within the
        documentation issued by Premium Credit. This right to withdraw only
        applies to the credit agreement and not the insurance policy. If you
        want to cancel the credit agreement, full payment for your insurance
        policy will be required.
      </p>
      <p>
        If you wish to cancel your Credit Agreement after 14 days, you may still
        do so by contacting us or Premium Credit. If you do, you will be
        required to pay the remaining finance balance in full. If you also need
        to cancel your insurance policy, you must contact us and not Premium
        Credit. Any rebate in premium due from your cancelled insurance policy
        will be calculated as per the 'What you will be charged if your
        insurance is cancelled' section of this document and it may be used to
        off set any outstanding balance under the finance agreement.
      </p>
      <p>
        <u>Adjustments</u>
      </p>
      <p>
        If, as the result of any adjustment to your insurance policy, there is
        an additional or return premium due, your remaining repayments will be
        adjusted, and a revised payment schedule sent to you. If, as a result of
        a validation process, your policy needs to be adjusted and there is an
        additional or return premium due, we shall write to you giving 7 days’
        notice of this amount and that it will be added to your remaining
        repayments. If you do not wish for this to happen, you will need to
        contact us before the date requested in our correspondence.
      </p>
      <h4>Who regulates us?</h4>
      <p>
        Car Care Plan Limited is authorised and regulated by the Financial
        Conduct Authority, No. 309268. Our permitted business is arranging and
        helping in the administration of general insurance and credit broking
        activities. You can check this on{' '}
        <a
          className='in-text'
          href='https://www.fca.org.uk/register/'
          target='_blank'>
          www.fca.org.uk/register
        </a>{' '}
        or by contacting the FCA on 0800 111 6768.
      </p>
      <h4>Are we covered by the Financial Services Compensation Scheme?</h4>
      <p>
        We are covered by the Financial Services Compensation Scheme (FSCS). You
        may be entitled to compensation from the scheme in the unlikely event we
        cannot meet our obligations to you. This depends on the type of
        insurance and the circumstances of the claim. Further information about
        the compensation arrangements is available from{' '}
        <a className='in-text' href='https://www.fscs.org.uk/' target='_blank'>
          www.fscs.org.uk
        </a>
      </p>
      <h4>What to do if you have a complaint</h4>
      <p>Please contact us:</p>
      <ul>
        <li>
          in writing to; {brandDisplayFullName} Insurance, Car Care Plan Ltd,
          Second Floor, Aztec Centre, Aztec West, Bristol BS32 4TD
        </li>
        <li>
          by calling the 'Complaints/Customer Service' telephone number listed
          on both our website and your documents
        </li>
        <li>
          or by emailing{' '}
          <Link className='in-text' to='#'>
            complaints@carcareplan.co.uk
          </Link>
        </li>
      </ul>
      <p>
        We will contact you within five days of receiving your complaint. In
        some cases, this will be to acknowledge your complaint, but in others,
        it may be to give you a full reply. If we cannot deal with your
        complaint within five working days, we will aim to give you a full reply
        within 28 days. In complex cases, or where further investigation is
        needed, this may take longer, and we will let you know if this is the
        case.
      </p>
      <p>
        Where the product purchased is an insurance product (regulated by the
        Financial Conduct Authority) and if it is not possible to reach an
        agreement, you have the right to ask the Financial Ombudsman Service to
        review your case. The right to apply to the Ombudsman must be exercised
        within six months of the date of our final decision. For more
        information, you can visit the Financial Ombudsman Service website at
        www.financial-ombudsman.org.uk, or you can write to:
      </p>
      <p>
        The Financial Ombudsman Service,
        <br />
        Exchange Tower,
        <br />
        London
        <br />
        E14 9SR
        <br />
        <br />
        Telephone: 0800 0234 567 or 0300 123 9123
      </p>
      <p>
        <b>Please note:</b> This complaints procedure does not affect any legal
        right you have to take action against us. Your product documentation and
        our Complaints Management Team will provide you with the correct
        referral process whether it is to the Financial Ombudsman or the Motor
        Ombudsman should you remain unhappy with the outcome to your complaint.
        Please note that not all complaints will be considered by FOS or the
        Motor Ombudsman.
      </p>
      <h4>At Renewal</h4>
      <p>
        For annual motor insurance and related add-ons we will attempt to
        contact you about your renewal requirements and provide you with your
        renewal quote within a reasonable period before your expiry date.
      </p>
      <p>
        We will automatically renew your annual motor insurance, related
        ancillary products and any related credit agreement each year unless:
      </p>
      <ul>
        <li>the renewal letter we send you says otherwise;</li>
        <li>you have opted out of this service;</li>
        <li>
          we do not hold a valid method of payment from you, be that:
          <ul>
            <li>
              a credit card or debit card with authority to hold the details for
              future transactions
            </li>
            <li>a direct debit instruction</li>
          </ul>
        </li>
        <li>
          we are unable to arrange cover for you, in which case we will contact
          you to notify of this.
        </li>
      </ul>
      <p>
        You can opt out of automatic renewal at the time of taking out your
        policy or any time after that by contacting our Customer Service Team.
      </p>
      <h4>Applicable law</h4>
      <p>
        English law will apply to these terms of business and the English courts
        shall have jurisdiction in any dispute unless you and we agree to apply
        the governing law and jurisdiction of the state in which you are
        resident within the UK. We supply the policy documents only in English
        and will always communicate with you in English.
      </p>
      <h4>Ways to Pay</h4>
      <p>
        You can pay for your policy in full by credit or debit card or you can
        pay monthly by Direct Debit via a premium Instalment plan. If you choose
        to pay by instalments, your personal information and bank details will
        be passed to Premium Credit – see the 'Payment of premium by Direct
        Debit (Premium Instalment Plan)' section of this document. We do not
        accept American Express or payments by credit/debit cards which have
        been issued from outside the UK.
      </p>
      <p>
        Where you are due a refund from us, we will endeavour to pay this back
        to you via the same method by which you paid within 30 days. If we
        refund you via cheque and this cheque remains uncashed you will have six
        years to claim this money under your statutory rights.
      </p>
      <h4>Important: Our right to cancel your insurance</h4>
      <p>
        If you choose to pay by Direct Debit, you are reminded that it is a term
        of the agreement that you authorise us to instruct the insurers to
        cancel your insurance if any Direct Debit or instalment payment, or any
        other amount due under those agreements, is in arrears and not paid on
        time. Should any payments be reclaimed by your bank under the Direct
        Debit Guarantee, the full balance of premium and associated charges will
        fall due immediately. If this payment is not made you authorise us to
        instruct the insurers to cancel your policy. If any credit or debit card
        payments used to pay for your policy are reclaimed by the card holder's
        bank the full balance of the premium and associated charges will fall
        due immediately. If this payment is not made you authorise us to
        instruct the insurers to cancel your policy.
      </p>
      <p>
        We also reserve the right to instruct the insurers to cancel your
        insurance in the following circumstances:
      </p>
      <ul>
        <li>
          A validation check or audit has resulted in additional information
          which means the cover is no longer valid.
        </li>
        <li>
          Documentation or contact from you that has been requested has not been
          received by the required date.
        </li>
        <li>
          An update to your policy (whether requested by you or following a
          validation check or audit) has resulted in an additional premium to
          pay, but,
          <ul>
            <li>You have not paid the amount due by the date requested</li>
            <li>You have refused to pay the amount requested</li>
          </ul>
        </li>
      </ul>
      <p>
        Wherever we instruct the insurer to cancel your insurance, we shall
        write to you giving 7 days' notice. In the event your policy is
        cancelled in this way, any fees our amounts payable will be calculated
        as per the 'What you will be charged if your insurance is cancelled' and
        'Payment of premium by Direct Debit (Premium Instalment Plan)' sections
        of this document.
      </p>
      <h4>About your information</h4>
      <p>
        You can view how we use your data in our Fair Use Notice. For more
        information please visit
        <Link
          className='in-text'
          to={PageAbsolutePath.Insurance.FairUseNoticePage}>
          {` ${brandVanityUrl}fairusenotice`}
        </Link>
        .
      </p>
      <p>
        This Terms of Business notice was last updated on 10 September 2024
        (V0013 - standard).
      </p>
      {!isShowingInPopup && (
        <div className='flex-row'>
          <ButtonBack
            name='btnBack'
            text='Back'
            onClick={goBack}
            additionalClassNames='btn-back'
          />
        </div>
      )}
    </div>
  );
};

export default BusinessTermsAncillaryPage;
